<script setup lang="ts">
import { InputType } from '~/static/enums/components/base-input'

interface Props {
  type?: InputType
  value: string | number | null
  placeholder?: string
  disabled?: boolean
  errorMessage?: string
  widthInput?: string
  enterkeyhint?: string
  inputmode?: 'text' | 'none' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search'
  testid?: string
}

const props = withDefaults(defineProps<Props>(), {
  id: '',
  name: '',
  errorMessage: '',
  type: InputType.Text,
  placeholder: '',
  disabled: false,
  widthInput: '',
  enterkeyhint: 'done',
  inputmode: 'text',
  testid: undefined,
})
const emit = defineEmits<{
  (e: 'input', val: string): void
  (e: 'change', val: string): void
  (e: 'enter', val: string): void
  (e: 'click'): void
}>()

const hasError = computed<boolean>(() => {
  return props.errorMessage !== undefined && props.errorMessage.length > 0
})

const computedInputmode = computed(() => {
  // Automatically set inputmode to 'numeric' for number inputs
  if (props.type === InputType.Number) {
    return 'numeric'
  }
  return props.inputmode
})

const inputKey = ref<number>(0)
const updateInput = () => {
  inputKey.value++
}

const handleInput = (event: Event) => {
  emit('input', (event.target as HTMLInputElement).value)
}
const handleChange = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  if (value !== (props.value ?? '').toString()) {
    if (value === '') {
      (event.target as HTMLInputElement).value = (props.value ?? '').toString()
    } else {
      emit('change', value)
    }
    updateInput()
  }
}
const handleEnter = (event: Event) => {
  let value = (event.target as HTMLInputElement).value
  const propsValue = (props.value ?? '').toString()
  if (value === '') {
    (event.target as HTMLInputElement).value = propsValue
    value = propsValue
  } else if (value !== propsValue) {
    emit('change', value)
  }
  emit('enter', value)
  updateInput()
}

const clickEmit = () => {
  emit('click')
}

const inputRef: Ref = ref(null)
const setFocus = () => {
  if (inputRef.value !== null) {
    (inputRef.value as HTMLInputElement).focus()
    // Некоторые устройства не открывают клавиатуру без клика
    ;(inputRef.value as HTMLInputElement).click()
  }
}

defineExpose({
  setFocus,
})

</script>

<template>
  <div
    class="input-group prevent_unselection"
    @click="clickEmit"
  >
    <div
      v-if="$slots['left']"
      class="form-control w-fit-content d-flex align-items-center form-control--first"
    >
      <slot name="left" />
    </div>
    <input
      :style="{'max-width': widthInput}"
      :value="value"
      :type="type"
      :key="inputKey"
      :placeholder="placeholder"
      :disabled="disabled"
      :class="{ 'is-invalid': hasError }"
      :enterkeyhint="enterkeyhint"
      :inputmode="computedInputmode"
      :data-testid="testid"
      @change="handleChange"
      @keyup.enter="handleEnter"
      @input="handleInput"
      class="form-control"
      ref="inputRef"
    >
    <div
      v-if="hasError"
      id="id"
      class="invalid-feedback"
    >
      {{ errorMessage }}
    </div>
    <div
      v-if="$slots['right']"
      class="form-control w-fit-content d-flex align-items-center form-control--last btn-icon"
    >
      <slot name="right" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.w-fit-content {
  max-width: fit-content;
}

.form-control {
  @include media-breakpoint-down(lg) {
    font-size: rem(14px);
    line-height: 150%;
    padding: rem(4px) rem(8px);
  }

  &--first {
    border-right: none !important;
  }
  
  &--last {
    margin-left: 0 !important;
    border-left: none !important;
  }
}
</style>
